import { useEffect } from "react";

const fbPixelSnippet = (pixelId) => `
  !function(f,b,e,v,n,t,s)
  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
  n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];
  s.parentNode.insertBefore(t,s)}(window, document,'script',
  'https://connect.facebook.net/en_US/fbevents.js');
  fbq('init', '${pixelId}');
  fbq('track', 'PageView');
`;

/**
 * Injects a page's analytics stack.
 *
 * @param tracking Per-page config from the backend. When omitted, the legacy
 *   stack above is used. When PRESENT, only what it declares is injected —
 *   so a page can be restricted to a single pixel with no other vendor
 *   loading on it. `fbq('track','Purchase')` reports to whichever pixels the
 *   page initialised, so conversion attribution follows the same scoping
 *   without extra wiring.
 *
 * Also installs the non-zoom viewport meta tag (iOS focus zoom fix).
 */
const useOfferAnalyticsScripts = (tracking, { viewportMeta = true } = {}) => {
  // Serialised so the effect re-runs only when the config actually changes —
  // an object literal would have a new identity on every render.
  // Note an empty object `{}` is a meaningful value: "inject nothing". That's
  // what the thank-you page passes while its config is still loading, so it
  // never briefly injects the legacy stack it is about to replace.
  const key = tracking ? JSON.stringify(tracking) : "";

  useEffect(() => {
    const cfg = key ? JSON.parse(key) : {};
    const head = document.getElementsByTagName("head")[0];
    const injected = [];

    const add = (el) => {
      head.appendChild(el);
      injected.push(el);
      return el;
    };

    if (viewportMeta) {
      const metaTag = document.createElement("meta");
      metaTag.name = "viewport";
      metaTag.content =
        "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no";
      add(metaTag);
    }

    if (cfg.plausibleDomain) {
      const plausible = document.createElement("script");
      plausible.defer = true;
      plausible.setAttribute("data-domain", cfg.plausibleDomain);
      plausible.src = "https://plausible.io/js/script.js";
      add(plausible);
    }

    if (cfg.pagesenseId) {
      const pagesense = document.createElement("script");
      pagesense.type = "text/javascript";
      pagesense.async = true;
      pagesense.src = `https://cdn-in.pagesense.io/js/aisensy/${cfg.pagesenseId}.js`;
      const firstScript = document.getElementsByTagName("script")[0];
      if (firstScript && firstScript.parentNode) {
        firstScript.parentNode.insertBefore(pagesense, firstScript);
        injected.push(pagesense);
      } else {
        add(pagesense);
      }
    }

    (cfg.metaPixels || []).forEach((pixelId, i) => {
      const script = document.createElement("script");
      script.id = `fb-pixel-${i}`;
      script.text = fbPixelSnippet(pixelId);
      add(script);

      const noscript = document.createElement("noscript");
      const img = document.createElement("img");
      img.height = "1";
      img.width = "1";
      img.style.display = "none";
      img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
      noscript.appendChild(img);
      add(noscript);
    });

    if (cfg.gtmId) {
      const gtm = document.createElement("script");
      gtm.text = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${cfg.gtmId}');`;
      add(gtm);
    }

    if (cfg.gaId) {
      const gtag = document.createElement("script");
      gtag.async = true;
      gtag.src = `https://www.googletagmanager.com/gtag/js?id=${cfg.gaId}`;
      add(gtag);

      const gtagConfig = document.createElement("script");
      gtagConfig.text = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${cfg.gaId}');
      `;
      add(gtagConfig);
    }

    return () => {
      injected.forEach((el) => {
        if (el && el.parentNode && el.parentNode.contains(el)) {
          el.parentNode.removeChild(el);
        }
      });
    };
  }, [key, viewportMeta]);
};

export default useOfferAnalyticsScripts;
